import { createContext, useReducer } from "react";
import { PodError, PodSearchResult } from "../../../api/pods.types";
import { SET_MARKETREGIONS, SET_PROVIDERLIST, SET_READY, SET_SEARCH_ERROR, SET_SEARCH_RESULTS, SET_IS_SEARCHING, SET_LOAD_ERROR, SET_USER_IS_MANAGER } from "./actions";

export interface MarketGroupOption {
  key: string,
  text: string,
  region: string,
  markets: string[],
}

export interface ProviderOption {
  id: string,
  npi: string,
  firstName: string,
  lastName: string,
  suffix: string,
  fullName: string,
  avatarUrl?: string,
  markets: string[],
}

export interface State {
  ready: boolean,
  error?: string,
  search: {
    query?: string,
    loading: boolean,
    results: PodSearchResult[],
    error?: PodError,
  },
  user: {
    isManager: boolean,
  }
  marketRegions: {
    // A loading boolean isn't needed here because the initial query for this data occurs prior to setReady.
    marketGroups: MarketGroupOption[],
    regions: string[],
  }
  providers: {
    physicians: ProviderOption[],
    others: ProviderOption[],
  },
}

export interface Action {
  type: string,
  data: any,
}

export const initialState: State = {
  ready: false,
  search: {
    loading: false,
    results: [],
  },
  user: {
    isManager: false,
  },
  marketRegions: {
    marketGroups: [],
    regions: [],
  },
  providers: {
    physicians: [],
    others: [],
  },
};

export const usePodReducer = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const useSelector = (callback: Function) => callback(state);

  return {
    state,
    dispatch,
    useSelector
  };
}

export const PodStateContext = createContext({
  useContextSelector: function<T>(callback: (state: State) => T) {return {} as T},
  contextDispatch: (() => {}) as React.Dispatch<Action>,
});

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case SET_READY:
      return {
        ...state,
        ready: action.data,
        error: undefined,
      };
    case SET_LOAD_ERROR:
      return {
        ...state,
        ready: false,
        error: action.data,
      }
    case SET_IS_SEARCHING:
      return {
        ...state,
        search: {
          query: action.data,
          loading: true,
          results: [],
        }
      };
    case SET_SEARCH_RESULTS:
      return {
        ...state,
        search: {
          query: state.search.query,
          loading: false,
          results: action.data
        }
      };
    case SET_SEARCH_ERROR:
      return {
        ...state,
        search: {
          query: state.search.query,
          loading: false,
          results: [],
          error: action.data,
        }
      };
    case SET_USER_IS_MANAGER:
      return {
        ...state,
        user: {
          isManager: action.data,
        }
      }
    case SET_MARKETREGIONS:
      return {
        ...state,
        marketRegions: {
          ...action.data,
          loading: false,
        }
      };
    case SET_PROVIDERLIST:
      return {
        ...state,
        providers: {
          ...action.data
        }
      };
    default:
      throw new Error(`Unrecognized action '${action.type}.'`);
  }
}